export interface BrowserInfo {
  name: string;
  version: number;
  isSupported: boolean;
}

export interface BrowserVersionConfig {
  chrome?: number;
  firefox?: number;
  safari?: number;
  edge?: number;
  opera?: number;
}

/**
 * Detects the current browser and version
 * @returns BrowserInfo object with name, version, and support status
 */
export const detectBrowser = (): Omit<BrowserInfo, 'isSupported'> => {
  const userAgent = navigator.userAgent;
  let browserName = '';
  let browserVersion = 0;

  // Chrome detection (must come before Safari detection as Chrome includes Safari in UA)
  if (userAgent.includes('Chrome/') && !userAgent.includes('Edg/')) {
    browserName = 'chrome';
    const match = userAgent.match(/Chrome\/(\d+)/);
    browserVersion = match ? parseInt(match[1], 10) : 0;
  }
  // Edge detection (Chromium-based Edge)
  else if (userAgent.includes('Edg/')) {
    browserName = 'edge';
    const match = userAgent.match(/Edg\/(\d+)/);
    browserVersion = match ? parseInt(match[1], 10) : 0;
  }
  // Firefox detection
  else if (userAgent.includes('Firefox/')) {
    browserName = 'firefox';
    const match = userAgent.match(/Firefox\/(\d+)/);
    browserVersion = match ? parseInt(match[1], 10) : 0;
  }
  // Safari detection
  else if (userAgent.includes('Safari/') && !userAgent.includes('Chrome/')) {
    browserName = 'safari';
    const match = userAgent.match(/Version\/(\d+)/);
    browserVersion = match ? parseInt(match[1], 10) : 0;
  }
  // Opera detection
  else if (userAgent.includes('OPR/')) {
    browserName = 'opera';
    const match = userAgent.match(/OPR\/(\d+)/);
    browserVersion = match ? parseInt(match[1], 10) : 0;
  }
  // Legacy Edge detection
  else if (userAgent.includes('Edge/')) {
    browserName = 'edge';
    const match = userAgent.match(/Edge\/(\d+)/);
    browserVersion = match ? parseInt(match[1], 10) : 0;
  }

  return {
    name: browserName,
    version: browserVersion
  };
};

/**
 * Checks if the current browser meets the minimum version requirements
 * @param browserVersionConfig Configuration object with minimum versions for each browser
 * @returns BrowserInfo object with support status
 */
export const checkBrowserVersion = (browserVersionConfig?: BrowserVersionConfig): BrowserInfo => {
  const browserInfo = detectBrowser();
  
  // If no configuration is provided, all versions are considered supported
  if (!browserVersionConfig) {
    return {
      ...browserInfo,
      isSupported: true
    };
  }

  const minVersion = browserVersionConfig[browserInfo.name as keyof BrowserVersionConfig];
  
  // If no minimum version is specified for this browser, consider it supported
  if (minVersion === undefined) {
    return {
      ...browserInfo,
      isSupported: true
    };
  }

  // Check if current version meets minimum requirement
  const isSupported = browserInfo.version >= minVersion;

  return {
    ...browserInfo,
    isSupported
  };
};

/**
 * Gets a user-friendly browser name for display purposes
 * @param browserName Internal browser name
 * @returns Display-friendly browser name
 */
export const getBrowserDisplayName = (browserName: string): string => {
  const displayNames: Record<string, string> = {
    chrome: 'Google Chrome',
    firefox: 'Mozilla Firefox',
    safari: 'Safari',
    edge: 'Microsoft Edge',
    opera: 'Opera'
  };

  return displayNames[browserName] || 'Unknown Browser';
};